import { useCallback, useEffect, useRef, useState, } from 'react';
import { useScrollLock } from './useMAScrollLock';
import { THRESHOLD, TAP_DURATION } from '../components/MagicApronAssistant/constants';

/**
 * Custom hook to handle the state and logic of a draggable drawer
 *
 * @param { DrawerCommon } props - The properties for the useDraggableDrawer hook.
 * @returns {{
 *   handlePointerDown: (event: TouchEvent) => void,
 *   handlePointerMove: (event: TouchEvent) => void,
 *   handlePointerUp: () => void,
 * }}
 */
export const useDraggableDrawer = (drawerState, setDrawerState, onClose) => {
  /**
   * @type {[DrawerStatus, React.Dispatch<React.SetStateAction<DrawerStatus>>]}
   */
  const startY = useRef(null);
  const endY = useRef(null);
  const isDragging = useRef(false);
  const tapTimeout = useRef(null);
  const { lockScroll, unlockScroll } = useScrollLock();
  const [dragDistance, setDragDistance] = useState(0);
  const handlePointerDown = useCallback((event) => {
    startY.current = event?.clientY || event.touches?.[0]?.clientY;
    endY.current = startY.current;
    lockScroll();

    tapTimeout.current = setTimeout(() => {
      if (startY.current !== null) {
        isDragging.current = true;
      }
    }, TAP_DURATION);
  }, [startY.current, endY.current, tapTimeout.current]);

  const handlePointerMove = useCallback((event) => {
    if (startY.current === null) return;

    endY.current = event?.clientY || event.touches?.[0]?.clientY;
    setDragDistance(endY.current);
    // TODO: Consider updating drawer height in real time
  }, [startY.current, endY.current]);

  const handleTap = useCallback(() => {
    setDrawerState((prev) => {
      return prev === 'OPEN' ? 'MINIMIZED' : 'OPEN';
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    if (startY.current !== null && endY.current !== null) {
      const deltaY = endY.current - startY.current;

      setDrawerState((prev) => {
        switch (prev) {
        case 'MINIMIZED':
          if (deltaY >= (THRESHOLD / 2)) {
            onClose();
            return 'CLOSED';
          }
          if (deltaY <= -THRESHOLD) {
            return 'OPEN';
          }
          break;

        case 'OPEN':
          if (deltaY >= THRESHOLD) {
            return 'MINIMIZED';
          }
          break;

        default:
          break;
        }

        return prev;
      });
      setDragDistance(null);
    }
  }, [drawerState, startY.current, endY.current]);

  const handlePointerUp = useCallback(() => {
    clearTimeout(tapTimeout.current);
    unlockScroll();

    if (isDragging.current) {
      handleDragEnd();
    } else {
      handleTap();
    }

    startY.current = null;
    endY.current = null;
    isDragging.current = false;
  }, [isDragging.current, startY.current, endY.current, handleDragEnd, handleTap, tapTimeout.current]);

  useEffect(() => {
    if (drawerState === 'OPEN' || isDragging.current) {
      lockScroll();
    }
    return () => {
      // unlock scroll when drawer is not open or dragging
      unlockScroll();
    };
  }, [drawerState]);

  return ({
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dragDistance,
  });
};
